import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/pages/home/components/Navbar';
import Footer from '@/pages/home/components/Footer';
import JsonLd from '@/components/seo/JsonLd';
import { SITE_URL, SITE_NAME } from '@/components/seo/site';
import { getArticleContent, articleSlugs } from '@/mocks/articles';

export default function Article() {
  const { t, i18n } = useTranslation();
  const { slug } = useParams<{ slug: string }>();
  const [openFaq, setOpenFaq] = useState<number | null>(0);

  const raw = i18n.language || 'en';
  const lang = raw.split('-')[0];
  const content = slug ? getArticleContent(slug, lang) : undefined;

  useEffect(() => {
    window.scrollTo(0, 0);
    setOpenFaq(0);
  }, [slug]);

  if (!content) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="px-4 md:px-6 py-20 text-center">
          <h1 className="font-heading text-2xl font-bold text-foreground-950">
            {t('article.notFound')}
          </h1>
          <Link
            to="/"
            className="mt-6 inline-flex px-6 py-3 rounded-md bg-primary-500 text-white font-semibold hover:bg-primary-600 transition-colors cursor-pointer"
          >
            {t('footer.home')}
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  const related = articleSlugs.filter((s) => s !== slug).slice(0, 4);

  return (
    <div className="min-h-screen bg-background-50">
      <JsonLd
        data={{
          '@context': 'https://schema.org',
          '@type': 'Article',
          headline: content.h1,
          description: content.metaDescription,
          inLanguage: lang,
          datePublished: '2026-01-01',
          dateModified: '2026-08-20',
          author: { '@type': 'Organization', name: SITE_NAME, url: SITE_URL },
          publisher: { '@type': 'Organization', name: SITE_NAME, url: SITE_URL },
          mainEntityOfPage: {
            '@type': 'WebPage',
            '@id': `${SITE_URL}/article/${slug}`,
          },
        }}
      />
      <JsonLd
        data={{
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: content.faq.map((item) => ({
            '@type': 'Question',
            name: item.q,
            acceptedAnswer: { '@type': 'Answer', text: item.a },
          })),
        }}
      />
      <JsonLd
        data={{
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
            {
              '@type': 'ListItem',
              position: 2,
              name: content.h1,
              item: `${SITE_URL}/article/${slug}`,
            },
          ],
        }}
      />
      <Navbar />
      <main className="px-4 md:px-6 py-8 md:py-12">
        <article className="max-w-3xl mx-auto">
          <nav className="flex items-center gap-2 text-sm text-foreground-500 flex-wrap">
            <Link to="/" className="hover:text-primary-700 cursor-pointer transition-colors">
              {t('footer.home')}
            </Link>
            <i className="ri-arrow-right-s-line text-foreground-400" />
            <span className="text-foreground-700">{t('nav.shoppingGuide')}</span>
          </nav>

          <header className="mt-6">
            <h1 className="font-heading text-2xl md:text-4xl font-bold leading-tight text-foreground-950">
              {content.h1}
            </h1>
            <div className="mt-4 flex flex-wrap items-center gap-3 text-xs text-foreground-500">
              <span className="inline-flex items-center gap-1.5">
                <i className="ri-calendar-line" />
                2026
              </span>
              <span className="inline-flex items-center gap-1.5">
                <i className="ri-time-line" />
                {t('article.minutes')}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <i className="ri-refresh-line" />
                {t('hero.cardUpdated')}
              </span>
            </div>
            <p className="mt-5 text-foreground-600 text-base leading-relaxed">{content.intro}</p>
          </header>

          <div className="mt-8 flex flex-col gap-8">
            {content.sections.map((section) => (
              <section key={section.heading}>
                <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-950">
                  {section.heading}
                </h2>
                {section.body.map((paragraph, idx) => (
                  <p key={idx} className="mt-3 text-foreground-600 text-base leading-relaxed">
                    {paragraph}
                  </p>
                ))}
              </section>
            ))}
          </div>

          <section className="mt-10">
            <div className="rounded-lg border border-background-200 bg-background-100 overflow-hidden">
              <div className="px-5 py-4 border-b border-background-200">
                <h2 className="font-heading font-semibold text-foreground-950 text-lg">
                  {t('article.tableTitle')}
                </h2>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm">
                  <thead>
                    <tr className="bg-background-50 text-foreground-500">
                      {content.table.headers.map((h) => (
                        <th key={h} className="px-5 py-3 font-medium whitespace-nowrap">
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {content.table.rows.map((row) => (
                      <tr key={row[0]} className="border-t border-background-200">
                        <td className="px-5 py-3 font-medium text-foreground-800 whitespace-nowrap">
                          {row[0]}
                        </td>
                        <td className="px-5 py-3 text-foreground-600">{row[1]}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </section>

          <section className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="rounded-lg border border-background-200 bg-white p-5">
              <h2 className="flex items-center gap-2 font-heading font-semibold text-foreground-950">
                <i className="ri-thumb-up-line text-primary-600" />
                {t('article.pros')}
              </h2>
              <ul className="mt-3 flex flex-col gap-2">
                {content.pros.map((item) => (
                  <li key={item} className="flex items-start gap-2 text-sm text-foreground-600">
                    <i className="ri-check-line text-primary-600 mt-0.5" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-lg border border-background-200 bg-white p-5">
              <h2 className="flex items-center gap-2 font-heading font-semibold text-foreground-950">
                <i className="ri-information-line text-accent-600" />
                {t('article.cons')}
              </h2>
              <ul className="mt-3 flex flex-col gap-2">
                {content.cons.map((item) => (
                  <li key={item} className="flex items-start gap-2 text-sm text-foreground-600">
                    <i className="ri-close-line text-accent-600 mt-0.5" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </section>

          <section className="mt-10">
            <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-950">
              {t('faq.title')}
            </h2>
            <div className="mt-5 flex flex-col gap-3">
              {content.faq.map((item, idx) => (
                <div key={item.q} className="rounded-lg border border-background-200 bg-white overflow-hidden">
                  <button
                    type="button"
                    onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                    className="w-full flex items-center justify-between gap-3 px-5 py-4 text-left cursor-pointer"
                  >
                    <span className="font-heading font-semibold text-foreground-950 text-sm md:text-base">
                      {item.q}
                    </span>
                    <i
                      className={`ri-add-line text-foreground-500 text-lg transition-transform ${
                        openFaq === idx ? 'rotate-45' : ''
                      }`}
                    />
                  </button>
                  {openFaq === idx && (
                    <div className="px-5 pb-4 text-foreground-600 text-sm leading-relaxed">{item.a}</div>
                  )}
                </div>
              ))}
            </div>
          </section>

          <section className="mt-10">
            <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-950">
              {t('article.related')}
            </h2>
            <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
              {related.map((s) => {
                const rel = getArticleContent(s, lang);
                if (!rel) return null;
                return (
                  <Link
                    key={s}
                    to={`/article/${s}`}
                    className="rounded-lg border border-background-200 bg-white p-4 hover:border-primary-300 cursor-pointer transition-colors"
                  >
                    <span className="font-heading font-semibold text-foreground-950 text-sm md:text-base">
                      {rel.h1}
                    </span>
                    <span className="mt-2 flex items-center gap-1.5 text-xs font-semibold text-primary-700">
                      {t('article.readGuide')}
                      <i className="ri-arrow-right-line" />
                    </span>
                  </Link>
                );
              })}
            </div>
          </section>

          <section className="mt-12 rounded-lg border border-background-200 bg-background-100 p-6 md:p-8 text-center">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">
              {t('cta.title')}
            </h2>
            <p className="mt-3 text-foreground-600 text-sm md:text-base max-w-xl mx-auto">
              {t('cta.subtitle')}
            </p>
            <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="px-6 py-3.5 rounded-md bg-primary-500 text-white font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
              >
                {t('cta.button1')}
              </a>
              <Link
                to="/"
                className="px-6 py-3.5 rounded-md border border-background-300 bg-white text-foreground-800 font-semibold whitespace-nowrap hover:bg-background-200 transition-colors cursor-pointer"
              >
                {t('footer.home')}
              </Link>
            </div>
          </section>
        </article>
      </main>
      <Footer />
    </div>
  );
}